import { calculator } from "../utils/calculator.js";

// Properties that any config might have, but exist only for technical reasons and do not affect component lifecycle
const DEFAULT_PROPERTIES = ["uid", "namespace"];
const DEFAULT_TYPE_VALUES = {
  string: "",
  enum: "",
  int: 0,
  float: 0,
  boolean: false,
};

const getDefaultPropertyValue = (descriptor) => {
  return descriptor?.default ?? DEFAULT_TYPE_VALUES[descriptor.type];
};

const getPropertyByKeyPath = (path, object) => {
  let result = object;
  for (let key of path) {
    result = result && result[key];
  }
  return result;
};
import { expressions } from "../utils/expressions.js";
import { events } from "./events.js";
import { nodes } from "./nodes.js";
import { store } from "./store.js";
import { createSingletonLogger } from "./singleton-logger.js";
import { linkedPropertiesStore, linkedSubscribersStore, isSubscriberProperty } from "./subscriptions.js";

class Properties {
  // Component identity for logging
  static componentName = "properties";
  
  #logger;
  
  constructor() {
    // Initialize the singleton logger
    this.#logger = createSingletonLogger(this);
  }
  // Function to retrieve the value of a property based on its UID
  _getPropertyValue(propertyUid) {
    const [uid, ...properties] = propertyUid.split(".");
    const node = nodes.get(uid);

    if (!node) {
      return null;
    }

    const key = properties.join(".");
    const value = node.props[key];
    if (value !== undefined) {
      return value;
    }

    return getPropertyByKeyPath(properties, node.props);
  }

  // Function to evaluate a non-reactive expression (snapshot or event token) within the given event context
  evaluateNonReactiveExpression(propertyExpression, event = events.currentEvent, defaultValue) {
    // TODO go through
    // if (!expressions.isNonReactive(propertyExpression)) {
    //   return propertyExpression;
    // }

    // Retrieve properties from the expression and process them
    const properties = expressions.retrieveProperties(propertyExpression, [
      expressions.getEventSpecialChar(),
      expressions.getSnapshotSpecialChar(),
    ]);

    const formattedProperties = properties.map((property) =>
      expressions.removeWhitespace(property),
    );
    const formattedNoBracketsProperties = formattedProperties.map((property) =>
      expressions.removeBrackets(property),
    );

    // Evaluate properties and retrieve values
    const resultValues = formattedNoBracketsProperties.flatMap((property, index) => {
      const evaluatedProperty = this.evaluateNonReactiveExpression(property, event, defaultValue);

      const isEvent = expressions.isPropertyEvent(formattedProperties[index]);
      if (isEvent) {
        // Handle event-based property retrieval
        const [_, ...keys] = evaluatedProperty.split(".");

        if (!event) {
          this.#logger.error(`No event is currently active. Value [${propertyExpression}] cannot be evaluated`);
          return [];
        }

        const value = getPropertyByKeyPath(keys, event);

        return {
          name: formattedProperties[index],
          value: value,
        };
      }

      if (expressions.isStoreProperty(evaluatedProperty)) {
        // Handle store property retrieval
        return {
          name: formattedProperties[index],
          value: store.get(evaluatedProperty, {
            logError: true,
          }),
        };
      }

      // Handle regular property retrieval
      const value = this._getPropertyValue(evaluatedProperty);
      return {
        name: formattedProperties[index],
        value,
      };
    });

    // Format and return the expression with evaluated values
    return this._formatExpressionWithEvaluatedProperties(propertyExpression, resultValues) ?? defaultValue;
  }

  // Backward-compatible alias
  evaluateNotLinkedExpression(...args) {
    return this.evaluateNonReactiveExpression(...args);
  }

  evaluateLinkedExpression(propertyExpression, propertyDescriptor, context = {}) {
    const defaultValue = propertyDescriptor ? getDefaultPropertyValue(propertyDescriptor) : null;
    const isString = propertyDescriptor?.type === "string";
    if (!expressions.isLinked(propertyExpression)) {
      return {
        value: propertyExpression ?? defaultValue,
        evaluatedPropertyUids: [],
      };
    }

    const properties = expressions.retrieveProperties(propertyExpression, [
      expressions.getLinkedPropertySpecialChar(),
    ]);

    const formattedProperties = properties.map((property) =>
      expressions.removeWhitespace(property),
    );
    const formattedNoBracketsProperties = formattedProperties.map((property) =>
      expressions.removeBrackets(property),
    );

    const resultValues = formattedNoBracketsProperties.flatMap((property, index) => {
      const { value: evaluatedProperty } = this.evaluateLinkedExpression(property, null, context);

      const value = this._getPropertyValue(evaluatedProperty);

      // TODO seems to be wrong, if the publisher doesn' exist then should either return 0 or empty string
      return {
        name: formattedProperties[index],
        value: value ?? defaultValue,
        propertyUid: property,
      };
    });

    const evaluatedPropertyUids = resultValues.map((value) => value.propertyUid);
    let formattedResultValue = this._formatExpressionWithEvaluatedProperties(propertyExpression, resultValues);
    if (isString) {
      formattedResultValue = String(formattedResultValue);
    }
    return {
      value: formattedResultValue,
      evaluatedPropertyUids,
    };
  }

  isLinked(uid, propertyName) {
    return isSubscriberProperty(uid, propertyName);
  }

  _formatExpressionWithEvaluatedProperties(originalExpression, evaluatedValues) {
    if (!evaluatedValues || evaluatedValues.length === 0) {
      return originalExpression;
    }

    const expressionWithoutFirstProperty = originalExpression.replace(evaluatedValues[0].name, "");

    if (evaluatedValues.length === 1 && expressionWithoutFirstProperty === "") {
      // Removed converting to different types, because property that is taken should has its original type.
      // In cases like account address that is usually a hexadecimal number, but we usually use it as a string,
      // we probably should explicitly convert such values somewhere.
      return evaluatedValues[0].value;
    }

    let result = originalExpression;
    for (let value of evaluatedValues) {
      result = result.replace(value.name, value.value ?? "");
    }

    const isExpression = expressions.isExpression(result);
    // TODO: isExpression() should not trigger on operators inside strings, hex codes, or non-mathematical contexts
    const containsHtmlTag = /<[^>]+>/.test(result);
    if (isExpression && !containsHtmlTag) {
      try {
        // Use the singleton calculator instance with setExpression
        const calculatedResult = calculator.setExpression(result).run();
        if (Number.isNaN(calculatedResult)) {
          throw new TypeError("Expression is not a number");
        }

        return calculatedResult;
      } catch {
        /* empty */
      }
    }

    return expressions.escapeExpression(result);
  }
}

const properties = new Properties();

const getPropertyMetadata = (uid, propertyName) => {
  const node = nodes.get(uid);
  if (!node) {
    return null;
  }

  const allDescriptors = store.get("descriptors");
  const descriptor = allDescriptors?.[node.namespace];
  const propertyDescriptor = descriptor?.properties[propertyName];
  if (!propertyDescriptor) {
    return null;
  }

  const type = propertyDescriptor.type;
  const currentValue = node.props[propertyName];

  if (typeof currentValue === "string" && type !== "string" && type !== "expression") {
    return { type, mode: "expression", resolved: false };
  }

  if (properties.isLinked(uid, propertyName)) {
    const matchesType =
      (type === "int" && Number.isInteger(currentValue)) ||
      (type === "float" && typeof currentValue === "number") ||
      (type === "boolean" && typeof currentValue === "boolean") ||
      (type === "string" && typeof currentValue === "string") ||
      (type === "enum" && typeof currentValue === "string");
    return { type, mode: "expression", resolved: matchesType };
  }

  return { type, mode: "literal", resolved: true };
};

export { Properties, properties, getDefaultPropertyValue, getPropertyByKeyPath, DEFAULT_PROPERTIES, getPropertyMetadata };